import React, { useEffect } from 'react';
import { getSelectionArrowAction } from '@waka/compat/selection-arrow-action';
import { nudgeTimelineSelection } from '@waka/compat/timeline-nudge';
import { changeItems } from '../state/actions/change-items';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';
import { useCurrentStateAsRef, useWriteContext } from '../utils/use-context';

const MOVE_STEP = 1;
const MOVE_STEP_WITH_MODIFIER = 10;

export const ArrowKeyMove: React.FC = () => {
  const { setState } = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEventTargetInputElement(e) || e.metaKey || e.ctrlKey) {
        return;
      }

      const base = stateAsRef.current;
      const action = getSelectionArrowAction(base, e.key, e.altKey);
      if (!action) return;
      const movableItems = action.ids;
      const step = e.shiftKey ? MOVE_STEP_WITH_MODIFIER : MOVE_STEP;
      const intent = action.intent;
      e.preventDefault();
      e.stopImmediatePropagation();

      // 타임라인에서 선택한 경우 좌/우 방향키는 시간 위치(item.from)를 이동한다.
      if (intent === 'timeline-time') {
        const frameDelta = e.key === 'ArrowLeft' ? -step : step;
        const planned = nudgeTimelineSelection(base, movableItems, frameDelta);
        if (planned === base) return;
        setState({
          update: (state) =>
            state.undoableState === base.undoableState &&
            state.selectedItems === base.selectedItems
              ? {
                  ...state,
                  undoableState: planned.undoableState,
                  selectedItems: planned.selectedItems,
                  timelineTimeEditApplied: true,
                }
              : state,
          commitToUndoStack: true,
        });
        return;
      }

      let deltaLeft = 0;
      let deltaTop = 0;

      switch (e.key) {
        case 'ArrowLeft':
          deltaLeft = -step;
          break;
        case 'ArrowRight':
          deltaLeft = step;
          break;
        case 'ArrowUp':
          deltaTop = -step;
          break;
        case 'ArrowDown':
          deltaTop = step;
          break;
      }

      setState({
        update: (state) =>
          state.undoableState !== base.undoableState ||
          state.selectedItems !== base.selectedItems
            ? state
            : changeItems(state, movableItems, (item) => ({
                ...item,
                left: item.left + deltaLeft,
                top: item.top + deltaTop,
              })),
        commitToUndoStack: true,
      });
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setState, stateAsRef]);

  return null;
};
